import _ from 'lodash';
import { useCallback, useRef } from 'react';
import { useLatest } from 'react-use';

/**
 * Hook that extends lodash's `throttle` function to create multiple throttled
 * functions split out by arg values.
 *
 * For example:
 * ```js
 * const log = ({ id, value }) => { console.info(id, value) };
 * const throttledFn = useThrottledFn(log, ['id'], 250);
 *
 * throttledFn({ id: 1, value: 'a' });
 * throttledFn({ id: 2, value: 'a' });
 * throttledFn({ id: 1, value: 'b' });
 * throttledFn({ id: 2, value: 'b' });
 * throttledFn({ id: 1, value: 'c' });
 * throttledFn({ id: 2, value: 'c' });
 *
 * // outputs:
 * // 1 a
 * // 2 a
 * // <250ms pause>
 * // 1 c
 * // 2 c
 * ```
 *
 * @template {string} ArgKey
 * @template FnReturn
 * @param {(args: Record<ArgKey, unknown>) => FnReturn} fn the function to throttle - must accept an object of args
 * @param {ArgKey[]} argKeys the keys of the args to use to split out throttled functions. NOTE: these args must be serializable.
 * @param {number} wait optional: lodash's `wait` arg - defaults to `250`
 * @param {any} options optional: lodash's `options` arg - defaults to `{ leading: true, trailing: true}`
 *
 * @returns {FnReturn}
 */
const useThrottledFn = (fn, argKeys = [], wait = 250, options = undefined) => {
  const throttledFns = useRef({});

  // We use `useLatest` here to ensure that useCallback is only evaluated once.
  const hookArgs = useLatest({ fn, argKeys, wait, options });

  return useCallback(
    (args) => {
      const {
        fn: fnRef,
        argKeys: argKeysRef,
        wait: waitRef,
        options: optionsRef,
      } = hookArgs.current;

      const key =
        argKeysRef.map((argKey) => args[argKey]).join('|') || 'default';

      if (!throttledFns.current[key]) {
        throttledFns.current[key] = _.throttle(fnRef, waitRef, optionsRef);
      }

      return throttledFns.current[key](args);
    },
    [hookArgs],
  );
};

export default useThrottledFn;
